import React, { createContext, useContext, useReducer, useCallback } from 'react';

const CommunityContext = createContext();

// Community action types
const COMMUNITY_ACTIONS = {
  // Forum actions
  SET_FORUM_POSTS: 'SET_FORUM_POSTS',
  ADD_FORUM_POST: 'ADD_FORUM_POST',
  UPDATE_FORUM_POST: 'UPDATE_FORUM_POST',
  REMOVE_FORUM_POST: 'REMOVE_FORUM_POST',

  // Activity feed actions
  SET_ACTIVITY_FEED: 'SET_ACTIVITY_FEED',
  ADD_ACTIVITY: 'ADD_ACTIVITY',

  // Community stats actions
  SET_COMMUNITY_STATS: 'SET_COMMUNITY_STATS',
  UPDATE_COMMUNITY_STATS: 'UPDATE_COMMUNITY_STATS',

  // Trade and challenge actions
  SET_TRADE_REQUESTS: 'SET_TRADE_REQUESTS',
  ADD_TRADE_REQUEST: 'ADD_TRADE_REQUEST',
  SET_CHALLENGES: 'SET_CHALLENGES',
  ADD_CHALLENGE: 'ADD_CHALLENGE',

  // Navigation
  SET_COMMUNITY_SUB_TAB: 'SET_COMMUNITY_SUB_TAB',

  // Reset actions
  RESET_COMMUNITY_DATA: 'RESET_COMMUNITY_DATA',
};

// Initial community state
const initialCommunityState = {
  // Forum data
  forumPosts: [],

  // Activity feed
  activityFeed: [],

  // Community statistics
  communityStats: {
    totalUsers: 0,
    activeTrades: 0,
    recentPosts: 0,
    challengeProgress: 0,
  },

  // Trade and challenge data
  tradeRequests: [],
  challenges: [],

  // Navigation
  communitySubTab: 'dashboard',
};

// Community reducer
const communityReducer = (state, action) => {
  switch (action.type) {
    // Forum actions
    case COMMUNITY_ACTIONS.SET_FORUM_POSTS:
      return {
        ...state,
        forumPosts: action.payload,
      };

    case COMMUNITY_ACTIONS.ADD_FORUM_POST:
      return {
        ...state,
        forumPosts: [action.payload, ...state.forumPosts],
      };

    case COMMUNITY_ACTIONS.UPDATE_FORUM_POST:
      return {
        ...state,
        forumPosts: state.forumPosts.map(post =>
          post.id === action.payload.id ? { ...post, ...action.payload } : post
        ),
      };

    case COMMUNITY_ACTIONS.REMOVE_FORUM_POST:
      return {
        ...state,
        forumPosts: state.forumPosts.filter(post => post.id !== action.payload),
      };

    // Activity feed actions
    case COMMUNITY_ACTIONS.SET_ACTIVITY_FEED:
      return {
        ...state,
        activityFeed: action.payload,
      };

    case COMMUNITY_ACTIONS.ADD_ACTIVITY:
      return {
        ...state,
        activityFeed: [action.payload, ...state.activityFeed.slice(0, 9)],
      };

    // Community stats actions
    case COMMUNITY_ACTIONS.SET_COMMUNITY_STATS:
      return {
        ...state,
        communityStats: action.payload,
      };

    case COMMUNITY_ACTIONS.UPDATE_COMMUNITY_STATS:
      return {
        ...state,
        communityStats: { ...state.communityStats, ...action.payload },
      };

    // Trade and challenge actions
    case COMMUNITY_ACTIONS.SET_TRADE_REQUESTS:
      return {
        ...state,
        tradeRequests: action.payload,
      };

    case COMMUNITY_ACTIONS.ADD_TRADE_REQUEST:
      return {
        ...state,
        tradeRequests: [action.payload, ...state.tradeRequests],
      };

    case COMMUNITY_ACTIONS.SET_CHALLENGES:
      return {
        ...state,
        challenges: action.payload,
      };

    case COMMUNITY_ACTIONS.ADD_CHALLENGE:
      return {
        ...state,
        challenges: [action.payload, ...state.challenges],
      };

    // Navigation
    case COMMUNITY_ACTIONS.SET_COMMUNITY_SUB_TAB:
      return {
        ...state,
        communitySubTab: action.payload,
      };

    // Reset actions
    case COMMUNITY_ACTIONS.RESET_COMMUNITY_DATA:
      return { ...initialCommunityState };

    default:
      return state;
  }
};

export const CommunityProvider = ({ children }) => {
  const [communityState, dispatch] = useReducer(communityReducer, initialCommunityState);

  // Forum actions
  const setForumPosts = useCallback(posts => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_FORUM_POSTS, payload: posts });
  }, []);

  const addForumPost = useCallback(post => {
    dispatch({ type: COMMUNITY_ACTIONS.ADD_FORUM_POST, payload: post });
  }, []);

  const updateForumPost = useCallback(postUpdate => {
    dispatch({ type: COMMUNITY_ACTIONS.UPDATE_FORUM_POST, payload: postUpdate });
  }, []);

  const removeForumPost = useCallback(postId => {
    dispatch({ type: COMMUNITY_ACTIONS.REMOVE_FORUM_POST, payload: postId });
  }, []);

  // Activity feed actions
  const setActivityFeed = useCallback(activities => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_ACTIVITY_FEED, payload: activities });
  }, []);

  const addActivity = useCallback(activity => {
    dispatch({ type: COMMUNITY_ACTIONS.ADD_ACTIVITY, payload: activity });
  }, []);

  // Community stats actions
  const setCommunityStats = useCallback(stats => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_COMMUNITY_STATS, payload: stats });
  }, []);

  const updateCommunityStats = useCallback(statsUpdate => {
    dispatch({ type: COMMUNITY_ACTIONS.UPDATE_COMMUNITY_STATS, payload: statsUpdate });
  }, []);

  // Trade and challenge actions
  const setTradeRequests = useCallback(trades => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_TRADE_REQUESTS, payload: trades });
  }, []);

  const addTradeRequest = useCallback(trade => {
    dispatch({ type: COMMUNITY_ACTIONS.ADD_TRADE_REQUEST, payload: trade });
  }, []);

  const setChallenges = useCallback(challenges => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_CHALLENGES, payload: challenges });
  }, []);

  const addChallenge = useCallback(challenge => {
    dispatch({ type: COMMUNITY_ACTIONS.ADD_CHALLENGE, payload: challenge });
  }, []);

  // Navigation actions
  const setCommunitySubTab = useCallback(tab => {
    dispatch({ type: COMMUNITY_ACTIONS.SET_COMMUNITY_SUB_TAB, payload: tab });
  }, []);

  // Utility actions
  const resetCommunityData = useCallback(() => {
    dispatch({ type: COMMUNITY_ACTIONS.RESET_COMMUNITY_DATA });
  }, []);

  const value = {
    // State
    ...communityState,

    // Forum actions
    setForumPosts,
    addForumPost,
    updateForumPost,
    removeForumPost,

    // Activity feed actions
    setActivityFeed,
    addActivity,

    // Community stats actions
    setCommunityStats,
    updateCommunityStats,

    // Trade and challenge actions
    setTradeRequests,
    addTradeRequest,
    setChallenges,
    addChallenge,

    // Navigation actions
    setCommunitySubTab,

    // Utility actions
    resetCommunityData,
  };

  return <CommunityContext.Provider value={value}>{children}</CommunityContext.Provider>;
};

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
};

// Export community action types and initial state for convenience
export { COMMUNITY_ACTIONS, initialCommunityState };
